/**
 * Servicio para subir archivos a través del Backend
 * SEGURO: No expone credenciales de Supabase en el frontend
 * 
 * El backend usa service_role key (secreta) para subir a Supabase Storage
 * El frontend solo envía el archivo al backend
 */

import { AnalyticsEvents } from './analytics';

interface UploadOptions {
  folder?: string; // Carpeta en Supabase Storage
  maxFileSize?: number; // Tamaño máximo en bytes (default: 50MB)
  onProgress?: (progress: number) => void; // Callback para reportar progreso (0-100)
}

/**
 * Obtiene la URL base de la API
 */
function getApiBaseUrl(): string {
  // Si hay una URL configurada explícitamente, usarla
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
  }
  // Por defecto, usar ruta relativa (funciona en Vercel)
  return '';
}

/**
 * Valida magic bytes del archivo en el frontend
 * IMPORTANTE: Esta validación es menos segura que la del backend, pero necesaria para uploads directos
 */
async function validateMagicBytesFrontend(file: File, mimeType: string): Promise<boolean> {
  // Para PDFs, leer más bytes para permitir espacios en blanco al inicio
  const readLength = mimeType === 'application/pdf' ? 1024 : 16;
  const buffer = await file.slice(0, readLength).arrayBuffer();
  const bytes = new Uint8Array(buffer);
  
  // Para PDFs, buscar %PDF en los primeros bytes (puede haber espacios antes)
  if (mimeType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
    // Convertir Uint8Array a Array para poder usar spread operator
    const bytesArray = Array.from(bytes.slice(0, 100));
    const pdfString = String.fromCharCode(...bytesArray);
    const hasPDFHeader = pdfString.includes('%PDF');
    // También verificar los primeros 4 bytes directamente
    const directMatch = bytes.length >= 4 && 
                       bytes[0] === 0x25 && bytes[1] === 0x50 && 
                       bytes[2] === 0x44 && bytes[3] === 0x46;
    return hasPDFHeader || directMatch;
  }
  
  // Magic bytes para otros tipos comunes
  const MAGIC_BYTES: { [key: string]: number[][] } = {
    'image/jpeg': [[0xFF, 0xD8, 0xFF]],
    'image/png': [[0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]],
    'image/gif': [[0x47, 0x49, 0x46, 0x38, 0x37, 0x61], [0x47, 0x49, 0x46, 0x38, 0x39, 0x61]],
    'image/webp': [[0x52, 0x49, 0x46, 0x46]], // RIFF (WebP tiene más bytes después)
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [[0x50, 0x4B, 0x03, 0x04]], // ZIP (DOCX es un ZIP)
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [[0x50, 0x4B, 0x03, 0x04]], // ZIP (XLSX es un ZIP)
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': [[0x50, 0x4B, 0x03, 0x04]], // ZIP (PPTX es un ZIP)
    'application/zip': [[0x50, 0x4B, 0x03, 0x04], [0x50, 0x4B, 0x05, 0x06], [0x50, 0x4B, 0x07, 0x08]],
  };
  
  const signatures = MAGIC_BYTES[mimeType];
  
  // Si no tenemos firma para este tipo, permitir (ej: SVG, texto plano, etc.)
  if (!signatures) {
    return true;
  }
  
  // Verificar si alguna firma coincide
  return signatures.some(signature => {
    if (bytes.length < signature.length) return false;
    return signature.every((byte, index) => bytes[index] === byte);
  });
}

/**
 * Sube un archivo al backend (que lo sube a Supabase de forma segura)
 */
export const uploadToSupabase = async (
  file: File,
  options: UploadOptions = {}
): Promise<string> => {
  const {
    folder = 'uploads',
    maxFileSize = 50 * 1024 * 1024, // 50MB por defecto
    onProgress
  } = options;

  // Validar tamaño del archivo
  if (file.size > maxFileSize) {
    const maxSizeMB = (maxFileSize / 1024 / 1024).toFixed(0);
    const error = new Error(`El archivo es demasiado grande. Tamaño máximo: ${maxSizeMB} MB`);
    // Track error de tamaño
    AnalyticsEvents.fileUploadError('file_too_large', file.type);
    throw error;
  }

  // Track inicio de subida
  const uploadStartTime = Date.now();
  const fileType = file.type || 'unknown';
  AnalyticsEvents.fileUploadStart(fileType, file.size, folder);

  // Crear FormData para enviar el archivo
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  // Reportar progreso inicial
  if (onProgress) {
    onProgress(5);
  }

  try {
    const apiBaseUrl = getApiBaseUrl();
    
    // Límite de Vercel: 4.5MB hard limit para serverless functions
    // Para archivos > 4MB, usar upload directo a Supabase (bypasea Vercel)
    const VERCEL_SIZE_LIMIT = 4 * 1024 * 1024; // 4MB (margen de seguridad)
    const useDirectUpload = file.size > VERCEL_SIZE_LIMIT;
    
    // Si el archivo es mayor a 4MB, usar upload directo a Supabase (bypasea Vercel)
    if (useDirectUpload) {
      
      // 0. Validar magic bytes en el frontend (antes de subir)
      // NOTA: Esta validación es menos segura que la del backend, pero necesaria para uploads directos
      const magicBytesValid = await validateMagicBytesFrontend(file, file.type || 'application/octet-stream');
      if (!magicBytesValid) {
        const error = new Error('El contenido del archivo no coincide con su tipo declarado.');
        AnalyticsEvents.fileUploadError('invalid_file_format', fileType);
        throw error;
      }
      
      // 1. Validar y obtener path del backend (con todas las validaciones de seguridad)
      const validateResponse = await fetch(`${apiBaseUrl}/api/validate-and-get-upload-path`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          fileName: file.name,
          folder: folder,
          contentType: file.type || 'application/octet-stream',
          fileSize: file.size
        })
      });

      if (!validateResponse.ok) {
        const errorData = await validateResponse.json().catch(() => ({ error: 'Error al validar archivo' }));
        throw new Error(errorData.error || 'Error al validar archivo');
      }

      const { path: filePath, publicUrl, originalUrl, bucket: bucketName } = await validateResponse.json();
      
      if (!filePath) {
        throw new Error('No se recibió el path del archivo');
      }
      
      // Guardar originalUrl para usarlo al final si está disponible
      let savedOriginalUrl = originalUrl;

      // 2. Usar cliente de Supabase directamente desde el frontend con anon_key
      // La anon_key es pública y segura - solo permite operaciones según políticas RLS del bucket
      const { createClient } = await import('@supabase/supabase-js');
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error(
          'Configuración de Supabase no encontrada. ' +
          'Se requiere REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_ANON_KEY para subir archivos grandes.'
        );
      }

      const supabaseClient = createClient(supabaseUrl, supabaseAnonKey);
      
      if (onProgress) {
        onProgress(10);
      }

      // Subir directamente a Supabase
      const { data: uploadData, error: uploadError } = await supabaseClient.storage
        .from(bucketName || 'files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type || 'application/octet-stream'
        });
      
      if (uploadError) {
        // StorageError de Supabase puede tener diferentes estructuras
        const errorStatus = (uploadError as any).statusCode || (uploadError as any).status || null;
        
        // Si es error de archivo existente, intentar con nombre único
        const isDuplicate = uploadError.message.includes('already exists') || 
                           uploadError.message.includes('duplicate') || 
                           errorStatus === 409;
        
        if (isDuplicate) {
          const timestamp = Date.now();
          const nameParts = file.name.split('.');
          const ext = nameParts.pop();
          const nameWithoutExt = nameParts.join('.');
          const uniqueName = `${nameWithoutExt}_${timestamp}.${ext}`;
          const uniquePath = folder ? `${folder}/${uniqueName}` : uniqueName;
          
          // Validar nuevo path también
          const retryValidateResponse = await fetch(`${apiBaseUrl}/api/validate-and-get-upload-path`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              fileName: uniqueName,
              folder: folder,
              contentType: file.type || 'application/octet-stream',
              fileSize: file.size
            })
          });

          if (!retryValidateResponse.ok) {
            throw new Error('Error al validar nombre único del archivo');
          }

          const { path: uniquePathValidated, publicUrl: uniquePublicUrl, originalUrl: uniqueOriginalUrl } = await retryValidateResponse.json();
          
          const { data: retryData, error: retryError } = await supabaseClient.storage
            .from(bucketName || 'files')
            .upload(uniquePathValidated, file, {
              cacheControl: '3600',
              upsert: false,
              contentType: file.type || 'application/octet-stream'
            });
          
          if (retryError) {
            throw new Error(`Error al subir archivo: ${retryError.message}`);
          }
          
          if (onProgress) {
            onProgress(100);
          }

          const uploadTime = Date.now() - uploadStartTime;
          AnalyticsEvents.fileUploadSuccess(fileType, file.size, folder, uploadTime);
          
          // Obtener la URL pública directamente de Supabase después de subir
          const { data: uniqueUrlData } = supabaseClient.storage.from(bucketName || 'files').getPublicUrl(uniquePathValidated);
          
          // Preferir originalUrl (URL completa de Supabase) sobre publicUrl (ruta relativa)
          return uniqueOriginalUrl || uniqueUrlData?.publicUrl || uniquePublicUrl;
        }
        
        // Manejar diferentes tipos de errores de Supabase
        // errorStatus ya está declarado arriba
        let errorMessage = 'Error al subir archivo';
        
        if (errorStatus === 400) {
          errorMessage = 'El nombre del archivo contiene caracteres no permitidos o el formato es inválido. Por favor, renombra el archivo y vuelve a intentar.';
        } else if (errorStatus === 403) {
          errorMessage = 'No tienes permisos para subir archivos a este directorio.';
        } else if (errorStatus === 413) {
          errorMessage = 'El archivo es demasiado grande.';
        } else {
          errorMessage = uploadError.message || 'Error desconocido al subir archivo';
        }
        
        const error: any = new Error(errorMessage);
        error.status = errorStatus || 500;
        error.statusCode = errorStatus || 500;
        AnalyticsEvents.fileUploadError(`supabase_${errorStatus || 'unknown'}`, fileType);
        throw error;
      }

      if (onProgress) {
        onProgress(100);
      }

      const uploadTime = Date.now() - uploadStartTime;
      AnalyticsEvents.fileUploadSuccess(fileType, file.size, folder, uploadTime);
      
      // Obtener la URL pública directamente de Supabase después de subir
      const { data: urlData } = supabaseClient.storage.from(bucketName || 'files').getPublicUrl(filePath);
      
      // Preferir originalUrl guardado o la URL pública de Supabase sobre publicUrl (ruta relativa)
      // Esto asegura que la URL tenga el formato correcto y preserve la capitalización
      return savedOriginalUrl || urlData?.publicUrl || publicUrl;
    }
    
    // Para archivos <= 4MB, usar el método tradicional (a través del backend)
    const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const uploadEndpoint = isPDF ? '/api/upload-file' : '/api/upload';
    
    // Usar XMLHttpRequest para poder reportar progreso
    const url = await new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.open('POST', `${apiBaseUrl}${uploadEndpoint}`, true);
      xhr.withCredentials = true; // Enviar cookies de sesión
      
      // Reportar progreso de subida
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          // Mapear progreso de 5% a 95% (dejando espacio para procesamiento)
          const percent = Math.round((event.loaded / event.total) * 90) + 5;
          onProgress(percent);
        }
      };
      
      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success && (response.url || response.originalUrl)) {
              if (onProgress) {
                onProgress(100);
              }
              // Track éxito de subida
              const uploadTime = Date.now() - uploadStartTime;
              AnalyticsEvents.fileUploadSuccess(fileType, file.size, folder, uploadTime);
              // Preferir originalUrl (URL completa de Supabase) sobre url (ruta relativa)
              resolve(response.originalUrl || response.url);
            } else {
              const error = new Error(response.error || 'Error desconocido al subir archivo');
              // Track error de subida
              AnalyticsEvents.fileUploadError('upload_failed', fileType);
              reject(error);
            }
          } catch (e) {
            const error = new Error('Error al procesar respuesta del servidor');
            AnalyticsEvents.fileUploadError('parse_error', fileType);
            reject(error);
          }
        } else {
          let errorMessage = `Error del servidor: ${xhr.status}`;
          let errorDetails = '';
          
          // Intentar parsear como JSON primero
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            errorMessage = errorResponse.error || errorMessage;
            errorDetails = errorResponse.details || '';
          } catch (e) {
            // Si no es JSON, puede ser una respuesta de texto plano de Vercel
            const responseText = xhr.responseText.trim();
            
            // Manejar respuestas específicas de Vercel
            if (xhr.status === 403) {
              if (responseText.includes('Forbidden')) {
                errorMessage = 'Acceso denegado. Verifica que tengas permisos para subir archivos.';
                errorDetails = 'El servidor rechazó la petición. Esto puede deberse a restricciones de seguridad o configuración.';
              } else {
                errorMessage = 'Acceso denegado por el servidor.';
              }
            } else if (xhr.status === 413) {
              if (responseText.includes('FUNCTION_PAYLOAD_TOO_LARGE') || responseText.includes('Request Entity Too Large')) {
                const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
                errorMessage = `El archivo es demasiado grande (${fileSizeMB} MB). Vercel tiene un límite de 4.5 MB para archivos subidos a través de serverless functions.`;
                errorDetails = 'Para archivos más grandes, contacta al administrador del sistema.';
              } else {
                errorMessage = 'El archivo es demasiado grande. El tamaño máximo permitido es 4.5 MB.';
              }
            } else if (xhr.status === 429) {
              errorMessage = 'Demasiadas solicitudes. Por favor, espera unos minutos antes de intentar nuevamente.';
            } else {
              // Usar el texto de respuesta si está disponible
              if (responseText && responseText.length > 0 && responseText.length < 200) {
                errorMessage = responseText;
              }
            }
          }
          
          // Crear error con status para detectar rate limiting y otros errores
          const error: any = new Error(errorMessage);
          error.status = xhr.status;
          error.statusCode = xhr.status;
          if (errorDetails) {
            error.details = errorDetails;
          }
          
          // Si es 403, intentar fallback a upload directo a Supabase
          if (xhr.status === 403) {
            // Intentar upload directo como fallback
            try {
              // 0. Validar magic bytes en el frontend (antes de subir)
              const magicBytesValid = await validateMagicBytesFrontend(file, file.type || 'application/octet-stream');
              if (!magicBytesValid) {
                AnalyticsEvents.fileUploadError('invalid_file_format', fileType);
                reject(new Error('El contenido del archivo no coincide con su tipo declarado.'));
                return;
              }
              
              // 1. Validar y obtener path del backend (con todas las validaciones de seguridad)
              const validateResponse = await fetch(`${apiBaseUrl}/api/validate-and-get-upload-path`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                  fileName: file.name,
                  folder: folder,
                  contentType: file.type || 'application/octet-stream',
                  fileSize: file.size
                })
              });

              if (!validateResponse.ok) {
                const errorData = await validateResponse.json().catch(() => ({ error: 'Error al validar archivo' }));
                AnalyticsEvents.fileUploadError('validation_failed', fileType);
                reject(new Error(errorData.error || 'Error al validar archivo'));
                return;
              }

              const { path: filePath, publicUrl, bucket: bucketName } = await validateResponse.json();
              
              if (!filePath) {
                reject(new Error('No se recibió el path del archivo'));
                return;
              }
              
              // 2. Usar cliente de Supabase directamente desde el frontend con anon_key
              const { createClient } = await import('@supabase/supabase-js');
              const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
              const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
              
              if (!supabaseUrl || !supabaseAnonKey) {
                reject(new Error(
                  'Configuración de Supabase no encontrada. ' +
                  'Se requiere REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_ANON_KEY para subir archivos.'
                ));
                return;
              }

              const supabaseClient = createClient(supabaseUrl, supabaseAnonKey);

              if (onProgress) {
                onProgress(50);
              }

              // Subir directamente a Supabase
              const { data: uploadData, error: uploadError } = await supabaseClient.storage
                .from(bucketName || 'files')
                .upload(filePath, file, {
                  cacheControl: '3600',
                  upsert: false,
                  contentType: file.type || 'application/octet-stream'
                });
              
              if (uploadError) {
                const errorStatus = (uploadError as any).statusCode || (uploadError as any).status || null;
                
                // Si es error de archivo existente, intentar con nombre único (igual que upload directo normal)
                const isDuplicate = uploadError.message.includes('already exists') || 
                                   uploadError.message.includes('duplicate') || 
                                   errorStatus === 409;
                
                if (isDuplicate) {
                  const timestamp = Date.now();
                  const nameParts = file.name.split('.');
                  const ext = nameParts.pop();
                  const nameWithoutExt = nameParts.join('.');
                  const uniqueName = `${nameWithoutExt}_${timestamp}.${ext}`;
                  
                  // Validar nuevo path también
                  const retryValidateResponse = await fetch(`${apiBaseUrl}/api/validate-and-get-upload-path`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                      fileName: uniqueName,
                      folder: folder,
                      contentType: file.type || 'application/octet-stream',
                      fileSize: file.size
                    })
                  });

                  if (!retryValidateResponse.ok) {
                    const errorData = await retryValidateResponse.json().catch(() => ({ error: 'Error al validar archivo' }));
                    AnalyticsEvents.fileUploadError('validation_failed', fileType);
                    reject(new Error(errorData.error || 'Error al validar nombre único del archivo'));
                    return;
                  }

                  const { path: uniquePathValidated, publicUrl: uniquePublicUrl } = await retryValidateResponse.json();
                  
                  const { data: retryData, error: retryError } = await supabaseClient.storage
                    .from(bucketName || 'files')
                    .upload(uniquePathValidated, file, {
                      cacheControl: '3600',
                      upsert: false,
                      contentType: file.type || 'application/octet-stream'
                    });
                  
                  if (retryError) {
                    const fallbackError: any = new Error(retryError.message || 'Error al subir archivo');
                    fallbackError.status = (retryError as any).statusCode || (retryError as any).status || 500;
                    fallbackError.statusCode = fallbackError.status;
                    AnalyticsEvents.fileUploadError(`supabase_fallback_retry_${fallbackError.status}`, fileType);
                    reject(fallbackError);
                    return;
                  }

                  if (onProgress) {
                    onProgress(100);
                  }

                  const uploadTime = Date.now() - uploadStartTime;
                  AnalyticsEvents.fileUploadSuccess(fileType, file.size, folder, uploadTime);
                  
                  resolve(uniquePublicUrl);
                  return;
                }
                
                // Si no es duplicado, manejar otros errores
                const fallbackError: any = new Error(uploadError.message || 'Error al subir archivo');
                fallbackError.status = errorStatus || 500;
                fallbackError.statusCode = errorStatus || 500;
                AnalyticsEvents.fileUploadError(`supabase_fallback_${errorStatus || 'unknown'}`, fileType);
                reject(fallbackError);
                return;
              }

              if (onProgress) {
                onProgress(100);
              }

              const uploadTime = Date.now() - uploadStartTime;
              AnalyticsEvents.fileUploadSuccess(fileType, file.size, folder, uploadTime);
              
              resolve(publicUrl);
              return;
            } catch (fallbackError: any) {
              // Si el fallback también falla, rechazar con el error original del 403
              AnalyticsEvents.fileUploadError('fallback_failed', fileType);
              reject(error); // Rechazar con el error original del 403
              return;
            }
          }
          
          // Track error de subida con código de estado
          AnalyticsEvents.fileUploadError(`http_${xhr.status}`, fileType);
          reject(error);
        }
      };
      
      xhr.onerror = () => {
        const error = new Error('Error de conexión. Verifica tu conexión a internet.');
        AnalyticsEvents.fileUploadError('network_error', fileType);
        reject(error);
      };
      
      xhr.ontimeout = () => {
        const error: any = new Error('La subida tardó demasiado tiempo. Intenta con un archivo más pequeño.');
        error.isTimeout = true;
        error.operationName = 'la subida del archivo';
        AnalyticsEvents.fileUploadError('timeout', fileType);
        reject(error);
      };
      
      xhr.onabort = () => {
        const error = new Error('La subida fue cancelada.');
        AnalyticsEvents.fileUploadError('aborted', fileType);
        reject(error);
      };
      
      // Timeout de 5 minutos para archivos grandes
      xhr.timeout = 5 * 60 * 1000;
      
      xhr.send(formData);
    });
    
    return url;
  } catch (error) {
    // Si el error no fue trackeado antes, trackearlo ahora
    if (error instanceof Error && !error.message.includes('Error desconocido')) {
      // Ya fue trackeado en los handlers específicos
    } else {
      AnalyticsEvents.fileUploadError('unknown_error', fileType);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Error desconocido al subir archivo');
  }
};

/**
 * Sube múltiples archivos
 */
export const uploadMultipleToSupabase = async (
  files: File[],
  options: UploadOptions = {}
): Promise<string[]> => {
  const uploadPromises = files.map(file => uploadToSupabase(file, options));
  return Promise.all(uploadPromises);
};

/**
 * Convierte una URL de Supabase a URL del dominio de la app
 * (para URLs que ya están guardadas en la base de datos)
 */
export function convertToProxyUrl(supabaseUrl: string): string {
  // Si ya es una URL del proxy, retornar tal cual
  if (!supabaseUrl || supabaseUrl.startsWith('/storage/files/')) {
    return supabaseUrl;
  }
  
  // Si no es una URL de Supabase, retornar tal cual
  if (!supabaseUrl.includes('supabase.co')) {
    return supabaseUrl;
  }
  
  try {
    // Extraer el path del archivo desde la URL de Supabase
    // Formato: https://[project].supabase.co/storage/v1/object/public/[bucket]/[path]
    const urlObj = new URL(supabaseUrl);
    const pathMatch = urlObj.pathname.match(/\/storage\/v1\/object\/public\/[^/]+\/(.+)$/);
    
    if (pathMatch && pathMatch[1]) {
      const filePath = pathMatch[1];
      return `/storage/files/${filePath}`;
    }
  } catch (error) {
    // Si hay error al parsear, retornar la URL original
  }
  
  return supabaseUrl;
}

/**
 * Verifica si una URL es de Supabase
 */
export const isSupabaseUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes('supabase.co') || url.startsWith('/storage/files/');
};

/**
 * Verifica si un valor es una URL válida
 */
export const isUrl = (value: string): boolean => {
  if (!value) return false;
  // URLs relativas del proxy también son válidas
  if (value.startsWith('/storage/files/')) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Asegura que el archivo o URL esté disponible como URL
 */
export const ensureSupabaseUrl = async (
  fileOrUrl: File | string,
  options: UploadOptions = {}
): Promise<string> => {
  if (typeof fileOrUrl === 'string') {
    if (isUrl(fileOrUrl)) {
      return fileOrUrl;
    }
    throw new Error('No se puede convertir base64 a URL. Por favor, sube el archivo nuevamente.');
  }
  return await uploadToSupabase(fileOrUrl, options);
};

/**
 * Elimina un archivo de Supabase Storage (a través del backend)
 * NOTA: Esta funcionalidad requiere implementar el endpoint en el backend
 */
export const deleteFromSupabase = async (url: string): Promise<void> => {
  // TODO: Implementar endpoint de eliminación en el backend si es necesario
  // Por ahora, los archivos no se eliminan automáticamente
};
